import React from 'react';
import type { OperationsTvRow } from './types';
import OperationsTvStatusPill from './OperationsTvStatusPill';

const aircraftFallbackSvg = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180"><defs><linearGradient id="g" x1="0" x2="1"><stop offset="0%" stop-color="#0f172a"/><stop offset="100%" stop-color="#1e293b"/></linearGradient></defs><rect width="320" height="180" fill="url(#g)"/><path d="M270 98v-8l-95-25V38c0-8-6-14-14-14s-14 6-14 14v27L52 90v8l95-12v31l-24 19v10l38-10 38 10v-10l-24-19V86z" fill="#f8fafc" opacity="0.95"/><text x="160" y="163" font-family="Arial" font-size="18" text-anchor="middle" fill="#fbbf24">MIRACLE AIR</text></svg>`
)}`;

const pilotFallback = (name: string) => {
  const initials = name
    .split(/\s+/g)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase() || '')
    .join('') || 'NA';
  return initials;
};

const OperationsTvRowCard: React.FC<{ row: OperationsTvRow; tvMode: boolean; rowIndex: number; animateIn: boolean }> = ({
  row,
  tvMode,
  rowIndex,
  animateIn,
}) => (
  // Aged telemetry rows are intentionally dimmed but kept readable for wall displays.
  <tr
    style={animateIn ? ({ ['--ops-tv-row-delay' as string]: `${Math.min(rowIndex * 80, 640)}ms` }) : undefined}
    className={`group border-b border-white/10 ${animateIn ? 'ops-tv-row-enter' : ''} ${
      row.status === 'LIVE' || row.status === 'IN FLIGHT' ? 'ops-tv-row-live' : ''
    } ${
      tvMode
        ? `bg-[linear-gradient(90deg,rgba(7,13,29,0.62),rgba(6,11,24,0.84))] ${row.status === 'DELAYED' || row.status === 'LAST SEEN' ? 'opacity-90 saturate-90' : row.status === 'OFFLINE' ? 'opacity-80 saturate-75' : 'opacity-100'}`
        : `bg-[linear-gradient(90deg,rgba(7,13,29,0.62),rgba(6,11,24,0.84))] transition hover:bg-[linear-gradient(90deg,rgba(14,26,55,0.72),rgba(10,20,42,0.9))] ${row.status === 'DELAYED' || row.status === 'LAST SEEN' ? 'opacity-90 saturate-90' : row.status === 'OFFLINE' ? 'opacity-80 saturate-75' : 'opacity-100'}`
    }`}
  >
    <td className={`${tvMode ? 'px-4 py-4.5' : 'px-4 py-3'}`}>
      <div className="flex items-center gap-3">
        <div className="relative h-14 w-24 overflow-hidden rounded-xl border border-amber-200/25 bg-[linear-gradient(145deg,rgba(10,16,30,0.96),rgba(5,9,20,0.99))] shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-1px_0_rgba(148,163,184,0.15),0_10px_24px_rgba(2,6,23,0.62)]">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_40%_0%,rgba(251,191,36,0.16),transparent_60%)]" />
          <div className="pointer-events-none absolute inset-0 rounded-xl border border-white/10" />
          <img
            src={row.aircraftImageUrl || aircraftFallbackSvg}
            alt={row.tailNumber}
            className={`h-full w-full scale-[1.035] object-cover ${row.status === 'OFFLINE' ? 'opacity-72 saturate-50' : row.status === 'DELAYED' || row.status === 'LAST SEEN' ? 'opacity-86 saturate-75' : 'opacity-95'}`}
            onError={(e) => {
              e.currentTarget.src = aircraftFallbackSvg;
            }}
          />
        </div>
        <div>
          <p className={`font-mono font-black tracking-tight text-white drop-shadow-[0_0_12px_rgba(148,163,184,0.3)] ${tvMode ? 'text-[32px]' : 'text-[25px]'}`}>{row.tailNumber}</p>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{row.aircraftName}</p>
        </div>
      </div>
    </td>

    <td className={`${tvMode ? 'px-4 py-4.5' : 'px-4 py-3'}`}>
      <p className="text-sm font-bold text-slate-100">{row.displayLocation || row.currentLocation || 'Unknown'}</p>
      {row.locationSubtext && <p className="text-[10px] uppercase tracking-[0.08em] text-slate-400">{row.locationSubtext}</p>}
    </td>

    <td className={`${tvMode ? 'px-4 py-4.5' : 'px-4 py-3'}`}>
      <OperationsTvStatusPill status={row.status} />
    </td>

    <td className={`${tvMode ? 'px-4 py-4.5' : 'px-4 py-3'}`}>
      <p className="text-sm font-black text-amber-100">
        {row.routeText || 'No Active Trip'}
      </p>
      {row.routeSubtext && row.routeSubtext !== row.routeText && <p className="text-[10px] uppercase tracking-[0.08em] text-slate-400">{row.routeSubtext}</p>}
    </td>

    <td className={`${tvMode ? 'px-4 py-4.5' : 'px-4 py-3'}`}>
      <div className="flex items-center gap-3">
        {row.pilotPhotoUrl ? (
          <img
            src={row.pilotPhotoUrl}
            alt={row.displayPilotName || row.pilotName}
            className={`h-10 w-10 rounded-full border border-amber-200/45 object-cover shadow-[0_0_16px_rgba(251,191,36,0.25)] ${row.status === 'OFFLINE' ? 'opacity-75 saturate-50' : row.status === 'DELAYED' || row.status === 'LAST SEEN' ? 'opacity-90 saturate-75' : ''}`}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-200/45 bg-[radial-gradient(circle_at_30%_20%,rgba(251,191,36,0.4),rgba(120,53,15,0.35))] text-[11px] font-black text-amber-100 shadow-[0_0_14px_rgba(251,191,36,0.2)]">
            {pilotFallback(row.pilotName)}
          </div>
        )}
        <div>
          <p className="text-sm font-bold text-slate-100">{row.displayPilotName || row.pilotName || 'No Pilot Assigned'}</p>
          <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">{row.pilotRole || 'Crew'}</p>
        </div>
      </div>
    </td>

    <td className={`${tvMode ? 'px-4 py-4.5' : 'px-4 py-3'}`}>
      <p className="font-mono text-sm font-black text-slate-100">{row.lastUpdateText}</p>
      <p className={`text-[10px] uppercase tracking-[0.1em] ${row.status === 'OFFLINE' ? 'text-rose-300' : row.status === 'DELAYED' || row.status === 'LAST SEEN' ? 'text-amber-300' : 'text-slate-400'}`}>
        {row.freshnessText || 'No signal'}
      </p>
    </td>

    <td className={`${tvMode ? 'px-6 py-4.5' : 'px-6 py-3'} text-right`}>
      <p
        className={`font-mono font-black ${
          row.etaText === '—'
            ? 'text-slate-400'
            : row.etaText === 'CALCULATING'
              ? 'text-amber-200 drop-shadow-[0_0_10px_rgba(245,158,11,0.35)]'
              : 'text-emerald-200 drop-shadow-[0_0_10px_rgba(16,185,129,0.35)]'
        } ${tvMode ? 'text-[34px]' : 'text-xl'}`}
      >
        {row.etaText}
      </p>
      {row.etaMinutes !== undefined
        ? <p className="text-[10px] uppercase tracking-[0.1em] text-emerald-300/80">ETA in {row.etaMinutes}m</p>
        : <p className="text-[10px] uppercase tracking-[0.1em] text-slate-500">No ETA</p>}
    </td>
  </tr>
);

export default React.memo(OperationsTvRowCard);
